import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const FeedbackSystem = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [feedbackType, setFeedbackType] = useState('');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const feedbackTypes = [
    { id: 'helpful', label: 'Very Helpful', icon: 'ThumbsUp', color: 'success' },
    { id: 'suggestion', label: 'Suggestion', icon: 'Lightbulb', color: 'warning' },
    { id: 'issue', label: 'Report Issue', icon: 'AlertCircle', color: 'danger' },
    { id: 'general', label: 'General Feedback', icon: 'MessageCircle', color: 'info' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setIsSubmitted(true);
    setIsSubmitting(false);
    
    // Hide after success
    setTimeout(() => {
      setIsVisible(false);
      setIsSubmitted(false);
      setFeedbackType('');
      setRating(0);
      setComment('');
    }, 2000);
  };

  const getColorClasses = (color) => {
    const colorMap = {
      success: 'bg-success-100 text-success hover:bg-success-200',
      warning: 'bg-warning-100 text-warning hover:bg-warning-200',
      danger: 'bg-danger-100 text-danger hover:bg-danger-200',
      info: 'bg-info-100 text-info hover:bg-info-200'
    };
    return colorMap[color] || colorMap.info;
  };

  return (
    <>
      {/* Feedback Button */}
      <div className="fixed bottom-4 left-4 z-40">
        <Button
          variant="secondary"
          size="sm"
          iconName="MessageSquare"
          onClick={() => setIsVisible(true)}
          className="shadow-strong"
        >
          Feedback
        </Button>
      </div>

      {/* Feedback Modal */}
      {isVisible && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-strong max-w-md w-full max-h-[80vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h2 className="font-heading font-bold text-xl text-text-primary">
                  Share Your Feedback
                </h2>
                <button
                  onClick={() => setIsVisible(false)}
                  className="text-text-muted hover:text-text-primary transition-colors"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
              <p className="text-text-secondary text-sm mt-2">
                Help us improve the Help Center experience
              </p>
            </div>

            {/* Content */}
            <div className="p-6">
              {isSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="CheckCircle" size={32} className="text-success" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                    Thank You!
                  </h3>
                  <p className="text-text-secondary">
                    Your feedback has been submitted and will help us improve our Help Center.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Feedback Type */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-3">
                      What type of feedback do you have?
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {feedbackTypes?.map((type) => (
                        <button
                          key={type?.id}
                          type="button"
                          onClick={() => setFeedbackType(type?.id)}
                          className={`p-3 rounded-lg border transition-all ${
                            feedbackType === type?.id
                              ? `${getColorClasses(type?.color)} border-current`
                              : 'border-border hover:border-primary-200'
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            <Icon name={type?.icon} size={16} />
                            <span className="text-sm font-medium">{type?.label}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Rating */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-3">
                      How would you rate your Help Center experience?
                    </label>
                    <div className="flex items-center space-x-1">
                      {[1, 2, 3, 4, 5]?.map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setRating(star)}
                          className={`p-1 transition-colors ${
                            star <= rating ? 'text-warning' : 'text-text-muted'
                          }`}
                        >
                          <Icon name="Star" size={24} className={star <= rating ? 'fill-current' : ''} />
                        </button>
                      ))}
                      <span className="ml-2 text-sm text-text-secondary">
                        {rating > 0 ? `${rating} star${rating > 1 ? 's' : ''}` : 'No rating'}
                      </span>
                    </div>
                  </div>

                  {/* Comment */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-3">
                      Additional comments (optional)
                    </label>
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Tell us more about your experience or suggestions for improvement..."
                      className="w-full p-3 border border-border rounded-lg focus:border-primary focus:outline-none resize-none"
                      rows={4}
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="flex items-center space-x-3">
                    <Button
                      type="submit"
                      variant="primary"
                      size="md"
                      iconName="Send"
                      disabled={!feedbackType || isSubmitting}
                      className="flex-1"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="md"
                      onClick={() => setIsVisible(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackSystem;